import React from 'react';
import { Info, Copy, AlertTriangle, Loader2 } from 'lucide-react';

export interface DnsRecord {
  type: string;
  name: string;
  value: string;
  description?: string;
}

interface DnsInstructionsProps {
  customDomain: string;
  records: DnsRecord[] | null;
  loading?: boolean;
  error?: string | null;
  /**
   * If true, the records shown are static fallback values rather than live
   * recommendations from Vercel — surfaces a hint to the user.
   */
  usingFallback?: boolean;
  onCopyToClipboard: (text: string) => void;
}

export const DnsInstructions: React.FC<DnsInstructionsProps> = ({
  customDomain,
  records,
  loading,
  error,
  usingFallback,
  onCopyToClipboard,
}) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
      <div className="flex items-start space-x-3">
        <Info className="h-5 w-5 text-blue-600 mt-0.5" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-blue-900 mb-4">Configure Your Custom Domain</h3>

          <div className="space-y-4">
            <p className="text-blue-800">
              Your custom domain <strong>{customDomain}</strong> has been saved! Follow these steps
              to complete the setup:
            </p>

            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">
                Step 1: Configure DNS with your domain registrar
              </h4>
              <p className="text-sm text-gray-600 mb-3">
                Add these DNS records at your domain registrar (GoDaddy, Namecheap, etc.):
              </p>

              {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 py-3">
                  <Loader2 size={16} className="animate-spin" />
                  Fetching the latest recommended records from Vercel…
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 mb-3">
                  <AlertTriangle size={16} className="mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              {usingFallback && !loading && !error && (
                <div className="flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 mb-3">
                  <AlertTriangle size={14} className="mt-0.5" />
                  <span>
                    Showing fallback DNS values — couldn't reach Vercel for the project-specific
                    recommendations. These should still work; refresh in a moment for the latest.
                  </span>
                </div>
              )}

              {records && records.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Type
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Name
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Value
                        </th>
                        <th className="px-4 py-2"></th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {records.map((r, idx) => (
                        <tr
                          key={`${r.type}-${r.name}-${r.value}-${idx}`}
                          className={r.type === 'TXT' ? 'bg-purple-50' : ''}
                          title={r.description}
                        >
                          <td className="px-4 py-2 text-sm font-mono">{r.type}</td>
                          <td className="px-4 py-2 text-sm font-mono">{r.name}</td>
                          <td className="px-4 py-2 text-sm font-mono break-all">{r.value}</td>
                          <td className="px-4 py-2">
                            <button
                              onClick={() => onCopyToClipboard(r.value)}
                              className="text-gray-400 hover:text-gray-600"
                              title="Copy value"
                            >
                              <Copy size={16} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {records && records.some(r => r.type === 'TXT') && (
                <p className="text-xs text-purple-800 mt-3">
                  TXT records are required by Vercel to verify ownership of the domain. Routing
                  won't activate until they're in place.
                </p>
              )}
            </div>

            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-2">
                Step 2: Verify Your Configuration
              </h4>
              <p className="text-sm text-gray-600">
                After adding the DNS records, use the "Verify Domain" button below to check if your
                configuration is correct. DNS changes typically take 1-2 hours to propagate, but can
                take up to 48 hours in some cases. Your site will remain accessible via the
                subdomain during this time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
